import { useEffect, useState, useMemo } from 'react';
import TableWrapperBase from '../shared/table';
import { ItemAPI } from '../../api/item';
import { Box, Link } from '@mui/material';
import { dataTypes } from '../shared/table-helpers';
import Modal from '../shared/modal';
import ItemCustomerUsageBreakdown from './item-customer-usage-breakdown';
import { sortTypes } from '../shared/sorting';
import { printRoundedNumber } from '../../shared/utils';
const TableWrapper = TableWrapperBase as any;

export interface ItemCustomerUsageProps {
  itemID: string;
  customerId?: string;
  customerName?: string;
  lastShipDate?: Date;
  salesRepName?: string;
  shipLegacyLocationId?: string;
  orderNo?: string;
  qtyShipped?: number;
  shsfLegacyLocationId?: string;
}

export default function ItemCustomerUsage({ itemID }: ItemCustomerUsageProps) {
  const [data, setData] = useState<ItemCustomerUsageProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breakdowns, setBreakdowns] = useState<
    Record<string, ItemCustomerUsageProps[]>
  >({});
  const [showbreakdownModalOpen, setShowbreakdownModalOpen] = useState(false);
  const [modalCustomerId, setModalCustomerId] = useState<string>('');
  const [modalCustomerName, setModalCustomerName] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await ItemAPI.getItemCustomerUsage({ itemID });
        const mappedData = response.data.map((row) => ({
          itemID: row.item_id,
          customerId: row.customer_id,
          customerName: row.customer_name,
          lastShipDate: row.last_ship_date,
          salesRepName: row.salesrep_name,
          shipLegacyLocationId: row.ship_legacy_location_id,
          isSummary: row.is_summary,
          orderNo: row.order_no,
          qtyShipped: row.qty_shipped,
          shsfLegacyLocationId: row.shsf_legacy_location_id,
        }));

        const summaryRows = mappedData.filter((row) => row.isSummary);
        setData(summaryRows);
        setLoading(false);

        const breakdownsByCustomer: Record<string, ItemCustomerUsageProps[]> =
          {};
        mappedData.forEach((row) => {
          if (!row.isSummary && row.customerId) {
            if (!breakdownsByCustomer[row.customerId])
              breakdownsByCustomer[row.customerId] = [];
            breakdownsByCustomer[row.customerId].push(row);
          }
        });
        setBreakdowns(breakdownsByCustomer);
      } catch (err) {
        setError('Failed to fetch customer usage data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [itemID]);

  const columns = [
    {
      id: 'customerId',
      label: 'Customer ID',
      dataType: (customerId) => (
        <Link href={`/customer?customerId=${customerId}`} target='_blank'>
          {customerId}
        </Link>
      ),
    },
    { id: 'customerName', label: 'Customer Name' },
    {
      id: 'qtyShipped',
      label: 'Quantity',
      sortByField: 'qtyShipped',
      isSortable: true,
      sortDataType: dataTypes.quantity,
      dataType: (qtyShipped, row) => {
        const breakdown = breakdowns[row.customerId || ''] || [];
        return breakdown.length > 0 ? (
          <Link
            onClick={() => {
              setModalCustomerId(row.customerId || '');
              setModalCustomerName(row.customerName || '');
              setShowbreakdownModalOpen(true);
            }}
          >
            {printRoundedNumber(qtyShipped, 0)}
          </Link>
        ) : (
          printRoundedNumber(qtyShipped, 0)
        );
      },
    },
    {
      id: 'lastShipDate',
      label: 'Last Ship Date',
      dataType: dataTypes.date,
      isSortable: true,
    },
    { id: 'shipLegacyLocationId', label: 'Warehouse' },
    { id: 'salesRepName', label: 'Consultant' },
  ];
  const rows = data.map((row) => ({ ...row }));

  const warehouseTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    const detailRows = Object.values(breakdowns).flat();
    detailRows.forEach((row) => {
      const warehouse = row.shipLegacyLocationId || 'Unknown';
      if (!totals[warehouse]) totals[warehouse] = 0;
      totals[warehouse] += row.qtyShipped || 0;
    });
    return totals;
  }, [breakdowns]);

  const overallTotal = useMemo(() => {
    const detailRows = Object.values(breakdowns).flat();
    return detailRows.reduce((sum, row) => sum + (row.qtyShipped || 0), 0);
  }, [breakdowns]);

  const warehouseNames = Object.keys(warehouseTotals).sort((a, b) => {
    const order = ['KY', 'UT'];
    const aIndex = order.indexOf(a);
    const bIndex = order.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  const totalsColumns = [
    ...warehouseNames.map((warehouse) => ({
      id: warehouse,
      label: warehouse,
      align: 'center',
      dataType: dataTypes.quantity,
    })),
    {
      id: 'total',
      label: 'Total',
      align: 'center',
      dataType: dataTypes.quantity,
    },
  ];
  const totalsRow = Object.fromEntries(
    warehouseNames.map((warehouse) => [
      warehouse,
      printRoundedNumber(warehouseTotals[warehouse], 0),
    ])
  );
  totalsRow['total'] = printRoundedNumber(overallTotal, 0);
  const totalsRows = [totalsRow];

  return (
    <>
      <Box sx={{ width: { xs: '100%', lg: '27%' } }}>
        <TableWrapper
          columns={totalsColumns}
          rows={totalsRows}
          style={{ height: '75px' }}
          showLoader={loading}
        />
      </Box>
      <TableWrapper
        showSearch
        transformRows
        orderBy={'lastShipDate'}
        orderDirection={sortTypes.desc}
        columns={columns}
        rows={rows}
        showLoader={loading}
      />
      {showbreakdownModalOpen && (
        <Modal
          open={showbreakdownModalOpen}
          setOpen={setShowbreakdownModalOpen}
          allowBackdropClose
        >
          <ItemCustomerUsageBreakdown
            breakdownData={(breakdowns[modalCustomerId] || []).map((bd) => ({
              orderNo: bd.orderNo,
              lastShipDate: bd.lastShipDate,
              shipLegacyLocationId: bd.shipLegacyLocationId,
              qtyShipped: bd.qtyShipped,
              shsfLegacyLocationId: bd.shsfLegacyLocationId,
              itemID,
            }))}
            customerName={modalCustomerName}
          />
        </Modal>
      )}
    </>
  );
}
